import {useTranslation} from 'react-i18next';
import React, {Fragment, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import _ from 'lodash';
import {FormLabel, IconButton, List, ListItem, MenuItem, Stack, Typography} from '@mui/material';
import Grid from '@mui/material/Grid';
import config from '../../../app/config';
import AgyleButton from '../../../components/ui/buttons/agyleButton';
import {AgyleNamedIcon} from '../../../components/icons/agyle';
import {admitBase, isChild} from '../admitPatient';
import moment from 'moment';
import Divider from '@mui/material/Divider';
import useOpenState from '../../../hooks/useOpenState';
import DialogContent from '@mui/material/DialogContent';
import InformationBox from '../../../components/other/informationBoxNew';
import clsx from 'clsx';
import Chip from '@mui/material/Chip';
import DialogActions from '@mui/material/DialogActions';
import Dialog from '@mui/material/Dialog';
import useDict from '../../../hooks/useDict';
import {useDispatch} from 'react-redux';
import DeleteIcon from '@mui/icons-material/Delete';
import MessageBox from '../../../util/messageBox';
import AgyleBlockSwitch from '../../../components/ui/switch/agyleBlockSwitch';
import AgyleDatePicker from '../../../components/ui/fields/agyleDatePicker';
import AgyleTextField from '../../../components/ui/fields/agyleTextField';
import AgyleDictionaryField from '../../../components/ui/selector/agyleDictionaryField';
import {setSnackbar} from '../../../store/reducers/globals';
import DialogTitle from '@mui/material/DialogTitle';
import {AccommodationStatus} from '../../../controls/agyle/ecds';
import ControlGP from '../../../controls/agyle/gp/ControlGP';
import ControlSurgery from '../../../controls/agyle/gp/ControlSurgery';
import AddressFinderControl from '../../../components/postCode/addressFinderControl';
import API from '../../../app/api';
import {useDebounce} from 'use-debounce';
import {useDemographicsStyles} from '../admitStyles';
import ChildDetails from '../components/childDetails';
import AdultAccordion from '../components/adultAccordion';
import UnknownPatientBanner from '../unknownPatientBanner';
import AgyleAccordion from '../../../components/ui/accordion/agyleAccordion';
import RemoveNextOfKinEntryModal from '../components/removeNextOfKinEntryModal';
import AddIcon from '@mui/icons-material/Add';

const phoneNumberTypes = [
  { value: "Home", label: "Home" },
  { value: "Work", label: "Work" },
  { value: "Mobile", label: "Mobile" },
  { value: "Other", label: "Other" },
  { value: "Alternate", label: "Alternate" },
];

const DemographicDetails = (props) => {
  const classes = useDemographicsStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // DIALOG OPEN STATES
  const [isDialogOpen, openDialog, closeDialog] = useOpenState(false, false);
  const [
    isNhsResultsWarningOpen,
    openNhsResultsWarning,
    closeNhsResultsWarning,
  ] = useOpenState(false, false);
  const [isRemoveDialogOpen, openRemoveDialog, closeRemoveDialog] =
    useOpenState(false, false);

  const [nhsNumberDebounced] = useDebounce(props.admit?.nhsNumber, 500);
  const [nhsNumberChanged, setNhsNumberChanged] = useState(false);
  const {isSmall} = props;

  // DICT VALUES
  const [ethnicOptions, isEthnicLoading] = useDict("EthnicGroups");
  const [languageOptions, isLanguageLoading] = useDict("Languages");
  const [genderOptions, isGenderLoading] = useDict(
    "Genders",
    "",
    "forAdmit eq true",
  );
  const [relationshipOptions, isRelationshipLoading] = useDict("Relationships");
  const [overseasOptions, isOverseasLoading] = useDict(
    "OverseasVisitorChargingCategory",
    "",
    "forAdmit eq true",
  );

  // ACCORDION EXPANDED STATE
  const [
    personalDetailsAccordionExpanded,
    setPersonalDetailsAccordionExpanded,
  ] = useState(true);
  const [contactAccordionExpanded, setContactAccordionExpanded] =
    useState(true);
  const [gpAndSurgeryAccordionExpanded, setGpAndSurgeryAccordionExpanded] =
    useState(true);
  const [nextOfKinAccordionExpanded, setNextOfKinAccordionExpanded] =
    useState(true);
  // HELPER INDEX STATE
  const [removedIndex, setRemovedIndex] = useState(null);

  const initPhone = {
    number: "",
    type: "Mobile",
  };
  const initEmail = {
    emailAddress: "",
  };
  const [phone, setPhone] = useState(initPhone);
  const [email, setEmail] = useState(initEmail);

  const nhsSearchResults = useRef(null);

  const {
    patientIsSelected,
    isPatientUpdated,
    addAdmit,
    admit,
    setAdmit,
    admission,
    setAdmission,
    setActiveStep,
    patientOnWhiteboard,
    isGenderMandatoryDialogOpen,
    closeGenderMandatoryDialog,
    speedRegFlag,
    fastReg,
    isTemporaryPatientEdit,
    temporaryPatientRef,
    temporaryPatientAttendanceRef,
    chiefComplaintOptions,
    isAddOnlyPatient,
    mergeToNewCreatedPatient,
    renderInfoSnackbar,
    conveyingAmbulanceOptions,
    conveyingAmbulanceLoading,
    validPostCode,
    setValidPostCode,
    validTemporaryPostCode,
    setValidTemporaryPostCode,
    toggleNFA,
    setToggleNFA,
    toggleNFATemporary,
    setToggleNFATemporary,
  } = props;

  const patientAge = moment().diff(admit.dob, "years");

  // const requiredForChild = (field) => patientAge < 18 && !admit[field] && !(fastReg || speedRegFlag);

  const checkDisabled = (type) => {
    if (
      (type === "contact" || type === "phone") &&
      config?.admitPatient?.contactNumbersReadOnly
    )
      return true;

    if (patientIsSelected && mergeToNewCreatedPatient) {
      return !isPatientUpdated;
    }

    if (admit.patientUnknown || patientIsSelected || isTemporaryPatientEdit) {
      return !(isPatientUpdated || isTemporaryPatientEdit);
    }
    if (!patientIsSelected || (patientIsSelected && isPatientUpdated)) {
      return false;
    }
  };

  const [disabled, setDisabled] = useState(true);

  useEffect(() => {
    if (admit.patientUnknown || patientIsSelected || isTemporaryPatientEdit) {
      setDisabled(!(isPatientUpdated || isTemporaryPatientEdit));
    }
    if (!patientIsSelected || (patientIsSelected && isPatientUpdated)) {
      setDisabled(false);
    }
  }, [
    patientIsSelected,
    isTemporaryPatientEdit,
    admit.patientUnknown,
    isPatientUpdated,
  ]);

  const handleOpenODSPortal = () => {
    window.open(config?.odsPortalUrl, "_blank").focus();
  };

  const handleChangeInput = (e) => {
    const { name, value } = e.target;
    setAdmit({
      ...admit,
      [name]: value,
    });
  };

  const testPostalCode = (input) => {
    const regex =
      config?.admitPatient?.customPostalCodeRegex ||
      /^[A-Za-z]{1,2}[0-9Rr][0-9A-Za-z]? [0-9][ABD-HJLNP-UW-Zabd-hjlnp-uw-z]{2}$/;
    return regex.test(input);
  };

  // Had to change to hook due "Save" button refreshment -> could save with wrong postal code
  useEffect(() => {
    if (config?.admitPatient?.postalCodeValidation) {
      setValidPostCode(
        _.isEmpty(admit?.postalCode) ? true : testPostalCode(admit?.postalCode),
      );
      setValidTemporaryPostCode(
        _.isEmpty(admit?.postalCodeTemporary)
          ? true
          : testPostalCode(admit?.postalCodeTemporary),
      );
    }
  }, [admit?.postalCodeTemporary, admit?.postalCode]);

  const handleChangeNhsNumber = (e) => {
    const cleanedNhs = e.target.value?.replace(/\s|[^0-9]/g, "");
    setNhsNumberChanged(true);

    setAdmit((prevState) => ({
      ...prevState,
      nhsNumber: cleanedNhs,
    }));
  };

  const handleChangeDob = (date) => {
    setAdmit((admit) => ({
      ...admit,
      dob: date,
    }));
  };

  const getAddressVal = (val, first) => {
    if (val) return `${first ? "" : " "}${val}`;
    return "";
  };

  const handleChangeAddress = (e) => {
    if (e) {
      setValidPostCode(true);
      if (!e?.subBuildingName && !e?.buildingName) {
        setAdmit({
          ...admit,
          street: `${getAddressVal(e?.buildingNumber, true)}${getAddressVal(e?.thoroughfareAndDescriptor)}`,
          postTown: getAddressVal(e?.posttown, true),
          postalCode: getAddressVal(e?.postcode, true),
          city: "",
        });
      } else {
        setAdmit({
          ...admit,
          street: `${getAddressVal(e?.subBuildingName, true)}${getAddressVal(e?.buildingName)}`,
          postTown: `${getAddressVal(e?.buildingNumber, true)}${getAddressVal(e?.thoroughfareAndDescriptor)}`,
          postalCode: getAddressVal(e?.postcode, true),
          city: getAddressVal(e?.posttown, true),
        });
      }
    }
  };

  const handleChangeAddressNok = (e, index) => {
    if (e) {
      let list = _.cloneDeep(admit.nokList);
      list[index] = {
        ...list[index],
        street: `${getAddressVal(e?.subBuildingName, true)}${getAddressVal(e?.buildingNumber)}${getAddressVal(e?.buildingName)}${getAddressVal(e?.thoroughfareAndDescriptor)}`,
        postalCode: getAddressVal(e?.postcode, true),
        city: getAddressVal(e?.posttown, true),
      };

      setAdmit((prevState) => ({
        ...prevState,
        nokList: list,
      }));
    }
  };

  const setAdmitToUnknown = useCallback(
    (unknownPatientState, returnObj = false, noSet = false) => {
      const baseAdmitState = admitBase();
      const unknownDob = moment(`01-01-${unknownPatientState.yearOfBirth}`);

      const objToPass = {
        ...baseAdmitState,
        patientUnknown: true,
        firstName: unknownPatientState.firstName,
        familyName: unknownPatientState.lastName,
        dob: unknownPatientState.dob || unknownDob,
        hospitalNumber: unknownPatientState.hospitalNumber,
        gender: unknownPatientState.gender,
        speedRegistry: unknownPatientState.speedRegistry,
      };

      if (!noSet) {
        setAdmit(objToPass);
      }

      if (returnObj) {
        return objToPass;
      }
    },
    [],
  );

  const handleAddNok = () => {
    const nokObject = {
      firstName: "",
      lastName: "",
      relationship: "",
      address: "",
      postalCode: "",
      phoneNumber: "",
    };

    if (!admit?.nokList || _.isEmpty(admit?.nokList)) {
      setAdmit((prevState) => ({
        ...prevState,
        nokList: [nokObject],
      }));
    } else {
      const newNokList = [...admit.nokList, nokObject];

      setAdmit((prevState) => ({
        ...prevState,
        nokList: newNokList,
      }));
    }
  };

  const handleChangeNok = (e, index) => {
    const { value, name } = e.target;
    const list = [...admit.nokList];

    list[index][name] = value;
    setAdmit((prevState) => ({
      ...prevState,
      nokList: list,
    }));
  };

  const handleChangeNokRelationship = (e, index) => {
    const list = [...admit.nokList];
    list[index].relationship = e;

    setAdmit((prevState) => ({
      ...prevState,
      nokList: list,
    }));
  };

  const handleRemoveNok = (index) => {
    const list = [...admit.nokList];
    const filteredList = list.filter((item, i) => i !== index);

    setAdmit((prevState) => ({
      ...prevState,
      nokList: filteredList,
    }));
  };

  const handleRemovePhoneNumber = (index) => {
    const list = [...admit.phoneNumbers];
    const filteredList = list.filter((item, i) => i !== index);

    setAdmit((prevState) => ({
      ...prevState,
      phoneNumbers: filteredList,
    }));
  };

  const handleRemoveEmail = (index) => {
    const list = [...admit.emailAdresses];
    const filteredList = list.filter((item, i) => i !== index);

    setAdmit((prevState) => ({
      ...prevState,
      emailAdresses: filteredList,
    }));
  };

  const handleSaveAddDialog = (addedItemKey) => {
    const value = addedItemKey === "email" ? email.emailAddress : phone.number;
    const itemKey = addedItemKey === "phone" ? "phoneNumbers" : "emailAdresses";

    if (!value) {
      return dispatch(
        setSnackbar({ message: "Please enter valid value.", type: "error" }),
      );
    }

    let tempAdmitArray;
    if (!_.isEmpty(admit[itemKey])) {
      tempAdmitArray = [
        ...admit[itemKey],
        addedItemKey === "phone" ? phone : email,
      ];
    } else {
      tempAdmitArray = [addedItemKey === "phone" ? phone : email];
    }

    setAdmit((prevState) => ({
      ...prevState,
      [itemKey]: tempAdmitArray,
    }));

    if (addedItemKey === "phone") {
      setPhone(initPhone);
    } else {
      setEmail(initEmail);
    }
  };

  const handleChangeNhsStatus = (value) => {
    setAdmit((prevState) => ({
      ...prevState,
      nhsBlockedProceeding: value,
    }));
  };

  const searchForNhs = async (nhsNumber) => {
    let options = {
      resourceUri: "/patients",
      text: nhsNumber,
      page: 1,
      pageSize: 100,
      options: {},
      sort: "familyName",
    };

    try {
      const patients = await API.getSearchList(options);
      if (patients.status === 200) {
        nhsSearchResults.current = patients.data.collection["__data"].filter(
          (element) => {
            return !!element.nhsNumber;
          },
        );
        if (nhsSearchResults.current.length === 1) {
          renderInfoSnackbar(
            "Patient found in database. Please select patient from search list.",
          );
        }
        if (nhsSearchResults.current.length > 1) {
          renderInfoSnackbar(
            "Multiple patients found in database. Please step to patient search and select valid patient from search list.",
          );
        }
        handleChangeNhsStatus(true);
        openNhsResultsWarning();
      } else {
        handleChangeNhsStatus(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (
        config?.admitPatient?.searchDbForNhsOnFirstRegister && (
            !patientIsSelected || (
                nhsNumberChanged &&
                props?.isPatientUpdated &&
                props.patientIsSelected
            )
        )
    ) {
      if (
        admit?.nhsNumber &&
        nhsNumberDebounced &&
        nhsNumberDebounced.length >= 10 &&
        admit?.nhsNumber === nhsNumberDebounced
      ) {
        searchForNhs(nhsNumberDebounced.trim());
      }
    }
  }, [
    props.patientIsSelected,
    config?.admitPatient?.searchDbForNhsOnFirstRegister,
    admit.nhsNumber,
    props?.isPatientUpdated,
    nhsNumberDebounced,
    nhsNumberChanged
  ]);

  useEffect(() => {
    if (!props?.isPatientUpdated && nhsNumberChanged) setNhsNumberChanged(false);
  }, [props?.isPatientUpdated, nhsNumberChanged])

  useEffect(() => {
    const defaultEthnicGroup =
      ethnicOptions &&
      ethnicOptions.find((item) => item.__description === "British");

    const defaultLanguage =
      languageOptions &&
      languageOptions.find((item) => item.__description === "English");

    const defaultGender =
      genderOptions &&
      genderOptions.find((item) => item.__description === "Not Known");

    const defaultOverseas =
      overseasOptions &&
      overseasOptions.find(
        (item) => item.__description === "Standard NHS -funded",
      );

    // if (!admit.gender) {
    //   setAdmit((prevState) => ({
    //     ...prevState,
    //     gender: defaultGender,
    //   }));
    // }

    if (!admit.overseasStatus && !fastReg && !speedRegFlag) {
      if (isAddOnlyPatient || !patientIsSelected || mergeToNewCreatedPatient) {
        return;
      }

      setAdmit((prevState) => ({
        ...prevState,
        overseasStatus: defaultOverseas,
      }));
    }

    if (admit && !admit.spokenLanguage && !fastReg && !speedRegFlag) {
      setAdmit((prevState) => ({
        ...prevState,
        spokenLanguage: defaultLanguage,
      }));
    }
  }, [languageOptions, overseasOptions]);

  useEffect(() => {
    const defaultLanguage =
      languageOptions &&
      languageOptions.find((item) => item.__description === "English");

    if (!admit?.spokenLanguageExists) {
      if (admit.patientNeedsInterpreter) {
        setAdmit((prevState) => ({
          ...prevState,
          spokenLanguage: null,
        }));
      } else {
        setAdmit((prevState) => ({
          ...prevState,
          spokenLanguage: defaultLanguage,
        }));
      }
    }
  }, [admit.patientNeedsInterpreter, languageOptions]);

  useEffect(() => {
    const changeEhic = () => {
      switch (admit.ehicCard) {
        case true:
          return setAdmit((prevState) => ({
            ...prevState,
            [prevState.noEhicReason]: "",
          }));
        case false:
          return setAdmit((prevState) => ({
            ...prevState,
            [prevState.ehicNumber]: "",
          }));
        case null:
          return setAdmit((prevState) => ({
            ...prevState,
            [prevState.ehicNumber]: "",
            [prevState.noEhicReason]: "",
          }));
        default:
          break;
      }
    };

    changeEhic();
  }, [admit.ehicCard]);

  useEffect(() => {
    if ((speedRegFlag || fastReg) && !admit.patientUnknown) {
      openDialog();
    }
  }, []);

  const renderSwitchField = (field, admitField, description, isError) => {
    const localValueField = field === "phone" ? "number" : "emailAddress";

    const changePhoneType = (e, localField, index) => {
      let numberList;
      const { value } = e.target;

      if (!_.isEmpty(admit.phoneNumbers) && field === "phone") {
        numberList = [...admit.phoneNumbers];

        const newNumber = {
          ...localField,
          type: value,
        };

        numberList.splice(index, 1, newNumber);

        setAdmit((prevState) => ({
          ...prevState,
          phoneNumbers: numberList,
        }));
      }
    };

    return (
      <>
        <Grid item xs={12} style={{ padding: field !== "phone" && 0 }} />
        <Grid item xs={12} sx={{ borderRadius: "10px" }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ flexWrap: "wrap", borderRadius: "10px" }}
            className={classes.whiteBorderContainer}
          >
            <FormLabel>{t(field === 'email' ? 'Email' : 'Phone Number')}</FormLabel>

            <Grid
              item
              xs={12}
              className={classes.phoneNumberContainer}
              sx={{ p: 1 }}
              style={{marginLeft: 0}}
            >
              <AgyleTextField
                margin="dense"
                type={field === "email" ? "email" : "tel"}
                multiline
                noBorder={false}
                disabled={checkDisabled(field)}
                error={isError}
                placeholder={field === 'email' ? 'Email' : 'Phone Number'}
                value={field === "email" ? email.emailAddress : phone.number}
                onChange={(e) => {
                  field === "email"
                    ? setEmail((prevState) => ({
                        ...prevState,
                        emailAddress: e.target.value,
                      }))
                    : setPhone((prevState) => ({
                        ...prevState,
                        number: e.target.value,
                      }));
                }}
              />

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  alignItems: "center",
                }}
              >
                <AgyleButton
                  onClick={() => handleSaveAddDialog(field)}
                  variant={"contained"}
                  size="small"
                  disabled={checkDisabled(field)}
                >
                  {t(`+ ${description}`)}
                </AgyleButton>
              </div>
            </Grid>
          </Stack>

          <Stack direction="column" alignItems="flex-start" sx={{mt: 2}}>
            {!_.isEmpty(admit[admitField]) &&
              admit[admitField].map((localField, index) => (
                <Fragment key={localField[localValueField]}>
                  <Grid key={index + "bcd"} item xs={12} sx={{ pl: 2 }}>
                    <div style={{display: 'flex', minWidth: '100%'}}>
                      <Typography
                        variant="h6"
                        component="div"
                        sx={{ pt: 0.5, mr: 2 }}
                      >
                        {index + 1}.
                      </Typography>

                      <div
                        className={classes.phoneNumberContainer}
                        style={{minWidth: '27px', gap: 10}}
                      >
                        <AgyleTextField
                          disabled
                          noBorder={false}
                          label={t(description)}
                          value={localField[localValueField]}
                          style={{
                            maxWidth: field === "phone" ? "180px" : "",
                            minWidth: field !== "phone" ? "240px" : "",
                          }}
                          onChange={() => console.error("Can't change field")}
                        />

                        {field === "phone" && (
                          <div style={{ minWidth: "100px" }}>
                            <AgyleTextField
                              label={t("Phone type")}
                              value={localField.type}
                              select
                              noBorder={false}
                              disabled={checkDisabled()}
                              onChange={(e) =>
                                changePhoneType(e, localField, index)
                              }
                            >
                              {phoneNumberTypes.map((option, index) => (
                                <MenuItem
                                  key={option.value + index}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                            </AgyleTextField>
                          </div>
                        )}

                        <IconButton
                          disabled={checkDisabled()}
                          onClick={() =>
                            field === "phone"
                              ? handleRemovePhoneNumber(index)
                              : handleRemoveEmail(index)
                          }
                          sx={{ ml: field !== "phone" ? -0.5 : 1 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </div>
                    </div>
                  </Grid>

                  <Grid key={"divider" + index} item xs={12}>
                    <Divider sx={{ my: 2 }} />
                  </Grid>
                </Fragment>
              ))}
          </Stack>
        </Grid>
      </>
    );
  };

  const renderGpDetails = () => {
    if (!admit.gp) {
      return;
    }

    // const { address } = admit.gp;
    return (
      <>
        <Grid item xs={12} sm={6} md={isSmall ? 12 : 4}>
          {admit.gp && (
            <AgyleTextField
              size={"large"}
              multiline
              disabled
              fullWidth
              noBorder={false}
              label={t("First Name")}
              value={admit.gp.firstName}
              onChange={() => {
                console.error("Can't change it.");
              }}
            />
          )}
        </Grid>

        {admit.gp && admit.gp?.phoneNumber && (
          <Grid item xs={12} sm={6} md={isSmall ? 12 : 4}>
            <AgyleTextField
              size={"large"}
              multiline
              fullWidth
              label={t("GP Phone Contact")}
              disabled
              noBorder={false}
              value={admit.gp?.phoneNumber}
              onChange={() => {
                console.error("Can't change it.");
              }}
            />
          </Grid>
        )}

        {admit.gp && admit.gp?.emailAddress && (
          <Grid item xs={12} sm={6} md={isSmall ? 12 : 4}>
            <AgyleTextField
              size={"large"}
              multiline
              fullWidth
              label={t("GP Email Contact")}
              disabled
              noBorder={false}
              value={admit.gp?.emailAddress}
              onChange={() => {
                console.error("Can't change it.");
              }}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={isSmall ? 12 : 4}>
          {admit.gp && (
            <AgyleTextField
              size={"large"}
              multiline
              disabled
              fullWidth
              noBorder={false}
              label={t("Postal Code")}
              value={admit.gp?.address?.postalCode}
              onChange={() => {
                console.error("Can't change it.");
              }}
            />
          )}
        </Grid>

        {admit.gp?.address?.lines &&
          admit.gp?.address?.lines.map((address, index) => {
            if (_.isEmpty(address)) return null;
            return (
              <Grid item xs={12} key={index}>
                <AgyleTextField
                  size={"large"}
                  multiline
                  disabled
                  fullWidth
                  noBorder={false}
                  label={t(`Address line ` + (index + 1))}
                  value={address}
                  onChange={() => {
                    console.error("Can't change it.");
                  }}
                />
              </Grid>
            );
          })}
      </>
    );
  };

  const renderTemporaryPatientWarning = () => {
    const chiefComplaintValue = chiefComplaintOptions.find(
      (chiefComplaint) =>
        chiefComplaint.__code ===
        temporaryPatientAttendanceRef.current?.initialAEComplaintCode,
    );
    const genderValue = genderOptions.find(
      (gender) => gender.__description === temporaryPatientRef.current.gender,
    );

    const chipSx = {
      width: {
        xs: "300px",
        sm: "250px",
      },
      height: "auto",
      padding: "6px 0",
      "& .MuiChip-label": {
        display: "block",
        whiteSpace: "normal",
      },
    }

    return (
      <Grid
        container
        direction={"row"}
        gap={1.51}
        justifyContent="flex-start"
        alignItems="stretch"
        sx={{
          background: "#FFC400",
          mt: 2,
          px: 2,
          py: 1,
          borderRadius: "10px",
        }}
      >
        <InformationBox type={"warning"}>
          <Typography variant="h6">
            {t(
              "CAUTION: Please compare and make sure that temporary and destination patient are correct.",
            )}
          </Typography>
        </InformationBox>

        <Chip
          color={
            temporaryPatientRef.current?.firstName !== admit?.firstName
              ? "error"
              : "info"
          }
          label={<b>First Name: {temporaryPatientRef.current?.firstName}</b>}
          sx={chipSx}
        />

        <Chip
          color={
            temporaryPatientRef.current?.familyName !== admit?.familyName
              ? "error"
              : "info"
          }
          label={<b>Family Name: {temporaryPatientRef.current?.familyName}</b>}
          sx={chipSx}
        />

        <Chip
          color={
            moment(temporaryPatientRef.current?.dob).toISOString() !==
            moment(admit?.dob).toISOString()
              ? "error"
              : "info"
          }
          label={
            <b>
              Date of Birth:{" "}
              {moment(temporaryPatientRef.current?.dob).format("DD/MM/YYYY")}
            </b>
          }
          sx={chipSx}
        />

        <Chip
          color={
            temporaryPatientRef.current?.gender !== admit.gender?.__description
              ? "error"
              : "info"
          }
          label={<b>Gender: {genderValue?.__description}</b>}
          style={{
            minHeight: "100%",
          }}
          sx={chipSx}
        />

        <Chip
          color="info"
          label={<b>Chief Complaint: {chiefComplaintValue?.parentTerm}</b>}
          sx={chipSx}
        />
      </Grid>
    );
  };

  useEffect(() => {
    if (admit.ehicNumber) {
      setAdmit((prevState) => ({
        ...prevState,
        ehicCard: true,
      }));
    }
  }, []);

  const genderValueValid = () => {
    if (!patientIsSelected) return true;

    const patientGender = admit.gender;
    const isMaleOrFemale =
      patientGender?.__description === "Male" ||
      patientGender?.__description === "Female";

    if (!isMaleOrFemale) {
      return false;
    }

    return true;
  };

  const handleToggleTemporaryAddressButton = () => {
    if (
      _.isNil(admit?.streetTemporary) ||
      _.isNil(admit?.postTownTemporary) ||
      _.isNil(admit?.cityTemporary) ||
      _.isNil(admit?.countyTemporary) ||
      _.isNil(admit?.postalCodeTemporary)
    ) {
      setAdmit((prevState) => ({
        ...prevState,
        streetTemporary: "",
        postTownTemporary: "",
        cityTemporary: "",
        countyTemporary: "",
        postalCodeTemporary: "",
      }));
    } else {
      setAdmit((prevState) => ({
        ...prevState,
        streetTemporary: null,
        postTownTemporary: null,
        cityTemporary: null,
        countyTemporary: null,
        postalCodeTemporary: null,
      }));
    }
  };

  const handleToggleNFA = (val, isPrimary) => {
    if (isPrimary) {
      setToggleNFA(val);
      if (val)
        handleChangeInput({ target: { name: "postalCode", value: "ZZ99 " } });
    } else {
      setToggleNFATemporary(val);
      if (val)
        handleChangeInput({
          target: { name: "postalCodeTemporary", value: "ZZ99 " },
        });
    }
  };

  const getTempAddressLabel = () =>
    _.isNil(admit?.streetTemporary)
      ? t("+ Add Temporary Address")
      : t("Remove Temporary Address");

  const renderTemporaryAddressInputs = useMemo(() => {
    return !_.isNil(admit?.streetTemporary);
  }, [admit?.streetTemporary]);

  const renderTemporaryAddressSection = () => {
    return (
      <>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Stack direction="row" gap={3} alignItems="center">
            <FormLabel style={{ marginLeft: 5, fontWeight: 600 }}>
              {t("Temporary Address")}
            </FormLabel>

            <AgyleButton
              onClick={handleToggleTemporaryAddressButton}
              variant="contained"
              disabled={checkDisabled()}
            >
              {getTempAddressLabel()}
            </AgyleButton>
          </Stack>
        </Grid>

        {renderTemporaryAddressInputs && (
          <Grid item xs={12} sm={isSmall ? 12 : 6}>
            <AgyleTextField
              size="large"
              disabled={checkDisabled()}
              fullWidth
              noBorder={false}
              label={t("Temporary Address Line 1")}
              value={admit?.streetTemporary}
              error={!admit?.streetTemporary}
              name="streetTemporary"
              onChange={handleChangeInput}
              style={{ marginBottom: 10 }}
              shrink
            />

            <AgyleTextField
              size="large"
              disabled={checkDisabled()}
              fullWidth
              noBorder={false}
              label={t("Temporary Address Line 2")}
              value={admit?.postTownTemporary}
              name="postTownTemporary"
              onChange={handleChangeInput}
              style={{ marginBottom: 10 }}
              shrink
            />

            <AgyleTextField
              size="large"
              disabled={checkDisabled()}
              fullWidth
              noBorder={false}
              label={t("Temporary Address Line 3")}
              value={admit?.cityTemporary}
              name="cityTemporary"
              onChange={handleChangeInput}
              style={{ marginBottom: 10 }}
              shrink
            />

            <AgyleTextField
              size="large"
              disabled={checkDisabled()}
              fullWidth
              noBorder={false}
              label={t("Temporary Address Line 4")}
              value={admit?.countyTemporary}
              name="countyTemporary"
              onChange={handleChangeInput}
              style={{ marginBottom: 10 }}
              shrink
            />

            <AgyleTextField
              size="large"
              disabled={checkDisabled()}
              fullWidth
              noBorder={false}
              label={t("Temporary Postal Code")}
              error={
                !admit?.postalCodeTemporary ||
                (!validTemporaryPostCode && !toggleNFATemporary)
              }
              value={admit?.postalCodeTemporary}
              name="postalCodeTemporary"
              onChange={handleChangeInput}
              shrink
              helperText={
                !validTemporaryPostCode &&
                !toggleNFATemporary &&
                !_.isEmpty(admit.postalCodeTemporary)
                  ? "Invalid Postal Code"
                  : ""
              }
            />

            {config?.admitPatient?.enableNFAButtonTemporary && (
              <div className={classes.buttonNFA}>
                <AgyleBlockSwitch
                  value={toggleNFATemporary}
                  onChange={handleToggleNFA}
                  disabled={checkDisabled()}
                  customLabels={{ yes: "NFA", no: "N/R" }}
                />

                <FormLabel style={{ marginLeft: 5 }}>
                  {t("No fixed abode")}
                </FormLabel>
              </div>
            )}
          </Grid>
        )}
      </>
    );
  };

  return (
    <Grid container gap={1} sx={{ pt: 2 }}>
      <Grid item xs={12}>
        <AgyleAccordion
          label={t("Personal Details")}
          expanded={personalDetailsAccordionExpanded}
          onChange={() =>
            setPersonalDetailsAccordionExpanded(
              !personalDetailsAccordionExpanded,
            )
          }
        >
          <Grid container spacing={2} sx={{ px: 2 }}>
            <div className={classes.bannerContainer}>
              {temporaryPatientRef.current && renderTemporaryPatientWarning()}

              {patientOnWhiteboard && (
                <InformationBox type={"warning"}>
                  <Typography variant="h6">
                    {t(
                      "Caution! This patient already has active attendance. Make sure to check whether patient data is correct.",
                    )}
                  </Typography>
                </InformationBox>
              )}

              {isPatientUpdated && (
                  <Stack sx={{mt: 2}}>
                    <InformationBox type={"warning"}>
                      <Typography variant="h6">
                        {t("Caution! You are editing patient data! Make sure to check whether patient data is correct.")}
                      </Typography>
                    </InformationBox>
                  </Stack>
              )}
            </div>

            {!patientIsSelected && (
              <Grid item xs={12} sx={{ my: 2 }}>
                <Grid container direction="row" alignItems="center">
                  <FormLabel style={{ flexGrow: 1 }} sx={{ minWidth: "185px" }}>
                    {t("Patient is unknown")}
                  </FormLabel>

                  <AgyleBlockSwitch
                    disabled={checkDisabled()}
                    onChange={() => {
                      openDialog();
                    }}
                    value={admit.patientUnknown}
                  />
                </Grid>
              </Grid>
            )}

            <Grid item xs={12}>
              <Grid container alignItems="center" justifyContent="flex-start">
                <Grid item xs={12}>
                  <Grid
                    container
                    alignItems="center"
                    style={{ minHeight: "46px" }}
                  >
                    <FormLabel
                      sx={{ mr: 1 }}
                      style={{ minWidth: "min-content", flexGrow: 1 }}
                    >
                      {t("Patient needs interpreter")}
                    </FormLabel>

                    <AgyleBlockSwitch
                      disabled={checkDisabled()}
                      onChange={(val) => {
                        setAdmit((prevState) => ({
                          ...prevState,
                          patientNeedsInterpreter: val,
                        }));
                      }}
                      value={admit.patientNeedsInterpreter}
                      customLabels={{ empty: t("Declined") }}
                      nullState
                      sx={{ flexGrow: 2 }}
                    />
                  </Grid>
                </Grid>

                {admit.patientNeedsInterpreter && (
                  <Grid
                    item
                    xs={12}
                    sx={{ py: 0.5 }}
                    minHeight={{ xs: 0, lg: "64px" }}
                  >
                    <AgyleDictionaryField
                      onChange={(_e, val) =>
                        setAdmit((prevState) => ({
                          ...prevState,
                          interpreterLanguage: val,
                        }))
                      }
                      noBorder={false}
                      disabled={checkDisabled()}
                      size={"large"}
                      options={languageOptions}
                      value={admit?.interpreterLanguage || null}
                      placeholder={t('Interpreter Language')}
                      loading={isLanguageLoading}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Grid item xs={12}>
                  <Grid
                    container
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "nowrap",
                      gap: 2,
                      minHeight: "46px",
                    }}
                  >
                    <FormLabel
                      sx={{ mr: 1 }}
                      style={{
                        flexGrow: 1,
                      }}
                    >
                      {t("EHIC Card")}
                    </FormLabel>

                    <AgyleBlockSwitch
                      disabled={checkDisabled()}
                      padding
                      onChange={(e) => {
                        setAdmit((prevState) => ({
                          ...prevState,
                          ehicCard: e,
                        }));
                      }}
                      nullState
                      value={admit.ehicCard}
                      customLabels={!true ? { no: t("N/R") } : null}
                    />
                  </Grid>
                </Grid>

                {(admit.ehicCard === false || admit.ehicCard) && (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{ pb: 0.5 }}
                    minHeight={{ xs: 0, md: "64px" }}
                  >
                    {admit.ehicCard && (
                      <AgyleTextField
                        size={"large"}
                        variant="outlined"
                        placeholder={'EHIC Number'}
                        fullWidth
                        noBorder={false}
                        disabled={checkDisabled()}
                        error={!admit.ehicNumber}
                        className={classes.formControl}
                        value={admit.ehicNumber}
                        name={"ehicNumber"}
                        onChange={handleChangeInput}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}

                    {admit.ehicCard === false && (
                      <AgyleTextField
                        size={"large"}
                        multiline
                        fullWidth
                        noBorder={false}
                        variant="outlined"
                        disabled={checkDisabled()}
                        error={!admit.noEhicReason}
                        placeholder={'Reason for not providing EHIC Card'}
                        className={classes.formControl}
                        name="noEhicReason"
                        value={admit.noEhicReason}
                        onChange={handleChangeInput}
                        InputLabelProps={{ shrink: true }}
                      />
                    )}
                  </Grid>
                )}
              </Grid>
            </Grid>

            <Grid
              item
              xs={12}
              sx={{ py: 0.5 }}
              minHeight={{ xs: 0, lg: "64px" }}
              pl={{ xs: 0, lg: 1 }}
            >
              <FormLabel>{t('Preferred Spoken Language')}</FormLabel>

              <AgyleDictionaryField
                onChange={(_e, val) =>
                  setAdmit((prevState) => ({
                    ...prevState,
                    spokenLanguage: val,
                  }))
                }
                size={"large"}
                noBorder={false}
                options={languageOptions}
                value={admit?.spokenLanguage || null}
                placeholder={t('Preferred Spoken Language')}
                disabled={checkDisabled()}
                loading={isLanguageLoading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={isSmall ? 12 : 4}>
              <FormLabel>{t('Hospital Number')}</FormLabel>

              <AgyleTextField
                size={"large"}
                fullWidth
                disabled
                readOnly
                noBorder={false}
                variant="outlined"
                placeholder={'Hospital Number'}
                className={classes.formControl}
                onChange={() => console.error("Cannot change this field")}
                value={admit.hospitalNumber}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={isSmall ? 12 : 4}>
              <FormLabel>{t('NHS Number')}</FormLabel>

              <AgyleTextField
                size={"large"}
                fullWidth
                multiline
                noBorder={false}
                disabled={
                  checkDisabled() ||
                  (patientIsSelected &&
                    !props.nhsNumberValid() &&
                    !isPatientUpdated)
                }
                readOnly={
                  patientIsSelected &&
                  !props.nhsNumberValid() &&
                  !isPatientUpdated
                }
                variant="outlined"
                placeholder={'NHS Number'}
                className={classes.formControl}
                value={admit.nhsNumber}
                name={"nhsNumber"}
                error={props.nhsNumberValid()}
                onChange={handleChangeNhsNumber}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={isSmall ? 12 : 4}>
              <FormLabel>{t('Date of Birth')}</FormLabel>

              <AgyleDatePicker
                placeholder={`Date of Birth`}
                size={"large"}
                noBorder={false}
                onChange={handleChangeDob}
                error={!admit.dob}
                disabled={
                  checkDisabled() ||
                  (patientIsSelected && !mergeToNewCreatedPatient)
                }
                readOnly={patientIsSelected && !mergeToNewCreatedPatient}
                value={admit.dob}
                type="date"
                disableFuture={true}
                maxDate={moment()}
                minDate={moment().subtract(140, "years")}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={isSmall ? 12 : 4}>
              <FormLabel>{t('First Name')}</FormLabel>

              <AgyleTextField
                size={"large"}
                fullWidth
                multiline
                noBorder={false}
                disabled={
                  checkDisabled() ||
                  (patientIsSelected && !mergeToNewCreatedPatient)
                }
                readOnly={patientIsSelected && !mergeToNewCreatedPatient}
                variant="outlined"
                placeholder={'First Name'}
                className={classes.formControl}
                value={admit.firstName}
                error={!admit.firstName}
                name={"firstName"}
                onChange={handleChangeInput}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={isSmall ? 12 : 4}>
              <FormLabel>{t('Middle Name')}</FormLabel>

              <AgyleTextField
                size={"large"}
                fullWidth
                multiline
                noBorder={false}
                disabled={
                  checkDisabled() ||
                  (patientIsSelected && !mergeToNewCreatedPatient)
                }
                readOnly={patientIsSelected && !mergeToNewCreatedPatient}
                variant="outlined"
                placeholder={'Middle Name'}
                className={classes.formControl}
                value={admit.middleNames}
                name={"middleNames"}
                onChange={handleChangeInput}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={isSmall ? 12 : 4}>
              <FormLabel>{t('Family Name')}</FormLabel>

              <AgyleTextField
                multiline
                noBorder={false}
                disabled={
                  checkDisabled() ||
                  (patientIsSelected && !mergeToNewCreatedPatient)
                }
                readOnly={patientIsSelected && !mergeToNewCreatedPatient}
                variant="outlined"
                placeholder={'Family Name'}
                error={!admit.familyName}
                fullWidth
                className={classes.formControl}
                value={admit.familyName}
                name={"familyName"}
                onChange={handleChangeInput}
                InputLabelProps={{ shrink: true }}
                size={"large"}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={isSmall ? 12 : 4}>
              <FormLabel>{t('Gender')}</FormLabel>

              <AgyleDictionaryField
                onChange={(_e, val) => {
                  setAdmit((prevState) => ({
                    ...prevState,
                    gender: val,
                  }));
                }}
                options={genderOptions}
                noBorder={false}
                disabled={
                  checkDisabled() ||
                  (patientIsSelected &&
                    !mergeToNewCreatedPatient &&
                    genderValueValid())
                }
                readOnly={
                  patientIsSelected &&
                  !mergeToNewCreatedPatient &&
                  genderValueValid()
                }
                size={"large"}
                error={!admit.gender}
                value={admit.gender}
                placeholder={t('Gender')}
                loading={isGenderLoading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={isSmall ? 12 : 4}>
              <FormLabel>{t('Ethnic Group')}</FormLabel>

              <AgyleDictionaryField
                onChange={(_e, val) => {
                  setAdmit((prevState) => ({
                    ...prevState,
                    ethnicGroup: val,
                  }));
                }}
                noBorder={false}
                options={ethnicOptions}
                disabled={checkDisabled()}
                size={"large"}
                value={admit.ethnicGroup}
                error={!admit.ethnicGroup}
                placeholder={t('Ethnic Group')}
                loading={isEthnicLoading}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={isSmall ? 12 : 4}>
              <FormLabel>{t('Overseas Visitor Charging Category')}</FormLabel>

              <AgyleDictionaryField
                onChange={(_e, val) => {
                  setAdmit((prevState) => ({
                    ...prevState,
                    overseasStatus: val,
                  }));
                }}
                noBorder={false}
                options={overseasOptions}
                disabled={checkDisabled()}
                size={"large"}
                error={!admit.overseasStatus}
                value={admit.overseasStatus}
                placeholder={t('Overseas Visitor Charging Category')}
                loading={isOverseasLoading}
              />
            </Grid>

            <Grid item xs={12} sm={8} md={isSmall ? 12 : 8}>
              <FormLabel>{t('Accommodation status')}</FormLabel>

              <AccommodationStatus
                placeholder={t('Accommodation status')}
                value={admit.accommodationStatus}
                onChange={(_e, val) => {
                  setAdmit({
                    ...admit,
                    accommodationStatus: val,
                  });
                }}
                size="large"
                noCodes
                noBorder={false}
                disabled={
                  checkDisabled() || isAddOnlyPatient || !patientIsSelected
                }
              />
            </Grid>

            {(isAddOnlyPatient ||
              !patientIsSelected ||
              mergeToNewCreatedPatient) && (
              <Grid item xs={12} md={isSmall ? 12 : 8}>
                <InformationBox type={"info"}>
                  {t(
                    "Optional elements of patient data only available to record on patient update.",
                  )}
                </InformationBox>
              </Grid>
            )}

            <Dialog
              open={isGenderMandatoryDialogOpen}
              onClose={closeGenderMandatoryDialog}
              disableEscapeKeyDown
            >
              <DialogTitle
                className={clsx(classes.background, classes.dialogTitle)}
              >
                {t("Gender not valid")}
              </DialogTitle>
              <DialogContent
                className={clsx(
                  classes.background,
                  classes.dialogContent,
                  "scroll-normal-white",
                )}
                dividers
              >
                <InformationBox type={"warning"} xs={12}>
                  <div className={clsx(classes.currentPatient)}>
                    <Typography variant="button" component="div">
                      {t(
                        "Please update patient's data and select valid gender to proceed.",
                      )}
                    </Typography>
                  </div>
                </InformationBox>
              </DialogContent>
              <DialogActions
                className={clsx(classes.background, classes.dialogContent)}
              >
                <AgyleButton
                  variant="contained"
                  onClick={closeGenderMandatoryDialog}
                >
                  {t("Close")}
                </AgyleButton>
              </DialogActions>
            </Dialog>

            <MessageBox
              open={isNhsResultsWarningOpen}
              onClose={closeNhsResultsWarning}
              title={t("NHS Number results warning.")}
              type={"warning"}
              haveAccept={{ yes: t("Accept"), no: t("Close") }}
              onAccept={() => {
                closeNhsResultsWarning();
                setActiveStep(0);
                props.setSearchValue(admit?.nhsNumber);
                props.searchPatients(admit?.nhsNumber);
              }}
              message={t(
                `There are active records for NHS Number <b>${admit?.nhsNumber}</b>. Please use patient search and select the patient from the list.`,
              )}
            />

            <Dialog
              open={isDialogOpen}
              // onClose={closeDialog}
              disableEscapeKeyDown
              sx={{ backdropFilter: "blur(5px)" }}
            >
              <UnknownPatientBanner
                genderOptions={genderOptions}
                setActiveStep={setActiveStep}
                setAdmitToUnknown={setAdmitToUnknown}
                onClose={closeDialog}
                addAdmit={addAdmit}
                admit={admit}
                setAdmit={setAdmit}
                admission={admission}
                setAdmission={setAdmission}
                fastReg={fastReg}
                speedRegFlag={speedRegFlag}
                arrivalModeOptions={props.arrivalModeOptions}
                isArrivalModeOptionsLoading={props.isArrivalModeOptionsLoading}
                chiefComplaintOptions={props.chiefComplaintOptions}
                isChiefComplaintOptionsLoading={
                  props.isArrivalModeOptionsLoading
                }
                conveyingAmbulanceOptions={conveyingAmbulanceOptions}
                conveyingAmbulanceLoading={conveyingAmbulanceLoading}
              />
            </Dialog>
          </Grid>
        </AgyleAccordion>
      </Grid>

      <Grid item xs={12}>
        <AgyleAccordion
          label={t("Contact")}
          expanded={contactAccordionExpanded}
          onChange={() =>
            setContactAccordionExpanded(!contactAccordionExpanded)
          }
        >
          <Grid container spacing={2} sx={{ p: 2 }}>
            {config.modules?.postCodeFinder?.enabled && (
              <Grid item xs={12}>
                <FormLabel>{t('Find Address')}</FormLabel>

                <AddressFinderControl
                  size="large"
                  noBorder={false}
                  placeholder={t("Find Address")}
                  onChange={handleChangeAddress}
                  disabled={checkDisabled()}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={isSmall ? 12 : 6}>
              <InformationBox type="info" noMargin>
                {t("Address Line 1 & Postal Code are mandatory in the address fields.")}
              </InformationBox>
            </Grid>
            <div style={{ flex: "1 0 0" }} />

            <Grid item xs={12} sx={{ mt: 2 }}>
              <Divider textAlign="left">{t("Primary Address")}</Divider>
            </Grid>

            <Grid item xs={12} sm={isSmall ? 12 : 4}>
              <FormLabel>{t('Address Line 1')}</FormLabel>

              <AgyleTextField
                size="large"
                disabled={checkDisabled()}
                fullWidth
                noBorder={false}
                placeholder={t('Address Line 1')}
                value={admit.street}
                error={!admit.street && _.isNil(admit?.streetTemporary)}
                name="street"
                onChange={handleChangeInput}
                style={{ marginBottom: 10 }}
                shrink
              />
            </Grid>

            <Grid item xs={12} sm={isSmall ? 12 : 4}>
              <FormLabel>{t('Address Line 2')}</FormLabel>

              <AgyleTextField
                size="large"
                disabled={checkDisabled()}
                fullWidth
                noBorder={false}
                placeholder={t('Address Line 2')}
                value={admit.postTown}
                name="postTown"
                onChange={handleChangeInput}
                style={{ marginBottom: 10 }}
                shrink
              />
            </Grid>

            <Grid item xs={12} sm={isSmall ? 12 : 4}>
              <FormLabel>{t('Address Line 3')}</FormLabel>

              <AgyleTextField
                size="large"
                disabled={checkDisabled()}
                fullWidth
                noBorder={false}
                placeholder={t('Address Line 3')}
                value={admit.city}
                name="city"
                onChange={handleChangeInput}
                style={{ marginBottom: 10 }}
                shrink
              />
            </Grid>

            <Grid item xs={12} sm={isSmall ? 12 : 4}>
              <FormLabel>{t('Address Line 4')}</FormLabel>

              <AgyleTextField
                size="large"
                disabled={checkDisabled()}
                fullWidth
                placeholder={t('Address Line 4')}
                value={admit.county}
                name="county"
                noBorder={false}
                onChange={handleChangeInput}
                style={{ marginBottom: 10 }}
                shrink
              />
            </Grid>

            <Grid item xs={12} sm={isSmall ? 12 : 4}>
              <FormLabel>{t('Postal Code')}</FormLabel>

              <AgyleTextField
                size="large"
                disabled={checkDisabled()}
                fullWidth
                noBorder={false}
                placeholder={t('Postal Code')}
                error={
                  (!admit?.postalCode && _.isNil(admit?.streetTemporary)) ||
                  (!validPostCode && !toggleNFA)
                }
                value={admit.postalCode}
                name="postalCode"
                onChange={handleChangeInput}
                shrink
                helperText={
                  !validPostCode && !toggleNFA && !_.isEmpty(admit.postalCode)
                    ? "Invalid Postal Code"
                    : ""
                }
              />
            </Grid>

            <Grid item xs={12} sm={isSmall ? 12 : 4}>
              {renderSwitchField(
                "phone",
                "phoneNumbers",
                "Phone Number",
                _.isEmpty(admit.phoneNumbers) &&
                  !phone.number &&
                  !config?.admitPatient?.contactNumbersReadOnly &&
                  config?.admitPatient?.contactNumberMandatory,
              )}
            </Grid>

            <Grid item xs={12}>
              {config?.admitPatient?.enableNFAButton && (
                <div className={classes.buttonNFA}>
                  <FormLabel style={{ marginLeft: 5 }}>
                    {t("No fixed abode")}
                  </FormLabel>

                  <div style={{ flex: "1 0 0" }}></div>
                  <AgyleBlockSwitch
                    value={toggleNFA}
                    onChange={(e) => handleToggleNFA(e, true)}
                    customLabels={{ yes: "NFA", no: "N/R" }}
                    disabled={checkDisabled()}
                  />
                </div>
              )}
            </Grid>

            {config?.admitPatient?.temporaryAddressEnabled &&
              renderTemporaryAddressSection()}
          </Grid>
        </AgyleAccordion>
      </Grid>

      <Grid item xs={12}>
        <AgyleAccordion
          label={t("GP & Surgery")}
          expanded={gpAndSurgeryAccordionExpanded}
          onChange={() =>
            setGpAndSurgeryAccordionExpanded(!gpAndSurgeryAccordionExpanded)
          }
        >
          <Grid container gap={2} sx={{ p: 2 }}>
            {(!admit.surgery || !admit.gp) && (
              <Grid item xs={12} md={6}>
                <InformationBox type="info" noMargin>
                  {t(
                    "GP surgery and GP are mandatory in the registration process.",
                  )}
                </InformationBox>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormLabel>{t('Select Practice')}</FormLabel>

              <ControlSurgery
                placeholder={t('Select Practice')}
                disabled={checkDisabled()}
                value={admit.surgery}
                error={!admit.surgery}
                noBorder={false}
                gp={admit.gp}
                onChange={(e) =>
                  setAdmit((prevState) => ({
                    ...prevState,
                    surgery: e,
                  }))
                }
                dontTrimSearch
              />
            </Grid>

            <Grid item xs={12}>
              <FormLabel>{t('Select GP')}</FormLabel>

              <ControlGP
                noBorder={false}
                placeholder={t('Select GP')}
                disabled={checkDisabled()}
                value={admit.gp}
                error={!admit.gp}
                surgery={admit.surgery}
                onChange={(e) =>
                  setAdmit((prevState) => ({
                    ...prevState,
                    gp: e,
                  }))
                }
                dontTrimSearch
              />
            </Grid>

            <Grid item xs={12}>
              <AgyleButton
                onClick={handleOpenODSPortal}
                variant="contained"
                selected
              >
                {t("Open ODS")}
              </AgyleButton>
            </Grid>

            {renderGpDetails()}
          </Grid>
        </AgyleAccordion>
      </Grid>

      <Grid item xs={12}>
        <AgyleAccordion
          label={t("Next of Kin")}
          expanded={nextOfKinAccordionExpanded}
          onChange={() =>
            setNextOfKinAccordionExpanded(!nextOfKinAccordionExpanded)
          }
        >
          <Grid container gap={2} sx={{ p: 2 }}>
            {_.isEmpty(admit.nokList) && (
              <Grid item xs={12}>
                <InformationBox type="info" noMargin>
                  {t(
                    "At least one Next of Kin is mandatory in the registration process.",
                  )}
                </InformationBox>
              </Grid>
            )}

            <Grid item xs={12} md={isSmall ? 12 : 2} sx={{ mt: 2 }}>
              <AgyleButton
                disabled={checkDisabled()}
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleAddNok()}
              >
                {t("Add Next of Kin")}
              </AgyleButton>
            </Grid>

            {!_.isEmpty(admit.nokList) && (
              <Grid item xs={12}>
                <List xs={12}>
                  {admit.nokList.map((nok, index) => {
                    return (
                      <ListItem
                        xs={12}
                        components="nav"
                        key={index + "nokIndex"}
                        sx={{ gap: 1, flexWrap: "wrap", pr: 0 }}
                      >
                        <Grid
                          container
                          spacing={2}
                          className={classes.background}
                          sx={{ p: 2, pt: 0, borderRadius: 1 }}
                        >
                          <Grid item xs={12}>
                            <Divider
                              textAlign="center"
                              style={{
                                position: "relative",
                              }}
                            >
                              {index + 1}
                            </Divider>

                            <IconButton
                              onClick={() => {
                                if (checkDisabled()) {
                                  return;
                                }
                                setRemovedIndex(index);
                                openRemoveDialog();
                              }}
                              disabled={checkDisabled()}
                              style={{
                                position: "absolute",
                                top: 0,
                                right: 0,
                                backgroundColor: "inherit",
                              }}
                            >
                              <AgyleNamedIcon name="trashBin" withTooltip />
                            </IconButton>
                          </Grid>

                          <Grid item xs={12} sm={6} md={isSmall ? 12 : 3}>
                            <FormLabel>{t('First Name')}</FormLabel>

                            <AgyleTextField
                              size={"large"}
                              fullWidth
                              multiline
                              noBorder={false}
                              disabled={checkDisabled()}
                              variant="outlined"
                              placeholder={'First Name'}
                              className={classes.formControl}
                              value={nok?.firstName}
                              name={"firstName"}
                              onChange={(e) => handleChangeNok(e, index)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={isSmall ? 12 : 3}>
                            <FormLabel>{t('Last Name')}</FormLabel>

                            <AgyleTextField
                              size={"large"}
                              fullWidth
                              multiline
                              noBorder={false}
                              disabled={checkDisabled()}
                              variant="outlined"
                              placeholder={'Last Name'}
                              className={classes.formControl}
                              value={nok?.lastName}
                              name={"lastName"}
                              onChange={(e) => handleChangeNok(e, index)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={isSmall ? 12 : 3}>
                            <FormLabel>{t('Relationship')}</FormLabel>

                            <AgyleDictionaryField
                              onChange={(_e, val) =>
                                handleChangeNokRelationship(val, index)
                              }
                              size={"large"}
                              options={relationshipOptions}
                              required
                              noBorder={false}
                              error={!nok?.relationship}
                              value={nok?.relationship || null}
                              placeholder={t('Relationship')}
                              disabled={checkDisabled()}
                              loading={isRelationshipLoading}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={isSmall ? 12 : 3}>
                            <FormLabel>{t('Contact')}</FormLabel>

                            <AgyleTextField
                              size={"large"}
                              fullWidth
                              multiline
                              noBorder={false}
                              disabled={checkDisabled("contact")}
                              variant="outlined"
                              placeholder={'Contact'}
                              className={classes.formControl}
                              value={nok?.phoneNumber}
                              name={"phoneNumber"}
                              onChange={(e) => handleChangeNok(e, index)}
                              InputLabelProps={{ shrink: true }}
                              required={
                                config?.admitPatient?.nokContactNumberMandatory
                              }
                              error={
                                config?.admitPatient
                                  ?.nokContactNumberMandatory &&
                                !nok.phoneNumber
                              }
                            />
                          </Grid>

                          {config.modules?.postCodeFinder?.enabled && (
                            <Grid item xs={12}>
                              <FormLabel>{t('Find Address')}</FormLabel>

                              <AddressFinderControl
                                size="large"
                                noBorder={false}
                                placeholder={t("Find Address")}
                                onChange={(e) =>
                                  handleChangeAddressNok(e, index)
                                }
                                disabled={checkDisabled()}
                              />
                            </Grid>
                          )}

                          <Grid item xs={12} sm={isSmall ? 12 : 4}>
                            <FormLabel>{t('Street')}</FormLabel>

                            <AgyleTextField
                              size={"large"}
                              multiline
                              noBorder={false}
                              disabled={checkDisabled()}
                              fullWidth
                              variant="outlined"
                              placeholder={'Street'}
                              className={classes.formControl}
                              value={nok?.street}
                              name={"street"}
                              onChange={(e) => handleChangeNok(e, index)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={isSmall ? 12 : 4}>
                            <FormLabel>{t('City')}</FormLabel>

                            <AgyleTextField
                              size={"large"}
                              multiline
                              disabled={checkDisabled()}
                              fullWidth
                              noBorder={false}
                              variant="outlined"
                              placeholder={'City'}
                              className={classes.formControl}
                              value={nok?.city}
                              name={"city"}
                              onChange={(e) => handleChangeNok(e, index)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={isSmall ? 12 : 4}>
                            <FormLabel>{t('Postal Code')}</FormLabel>

                            <AgyleTextField
                              size={"large"}
                              fullWidth
                              multiline
                              noBorder={false}
                              disabled={checkDisabled()}
                              variant="outlined"
                              placeholder={'Postal Code'}
                              className={classes.formControl}
                              value={nok?.postalCode}
                              name={"postalCode"}
                              onChange={(e) => handleChangeNok(e, index)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={isSmall ? 12 : 6}>
                            <FormLabel>{t('County')}</FormLabel>

                            <AgyleTextField
                              size={"large"}
                              multiline
                              noBorder={false}
                              disabled={checkDisabled()}
                              fullWidth
                              variant="outlined"
                              placeholder={'County'}
                              className={classes.formControl}
                              value={nok?.county}
                              name={"county"}
                              onChange={(e) => handleChangeNok(e, index)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6} md={isSmall ? 12 : 6}>
                            <FormLabel>{t('Post Town')}</FormLabel>

                            <AgyleTextField
                              size={"large"}
                              multiline
                              noBorder={false}
                              disabled={checkDisabled()}
                              fullWidth
                              variant="outlined"
                              placeholder={'Post Town'}
                              className={classes.formControl}
                              value={nok?.postTown}
                              name={"postTown"}
                              onChange={(e) => handleChangeNok(e, index)}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </Grid>
                      </ListItem>
                    );
                  })}
                </List>
              </Grid>
            )}

            {admit.dob && isChild(patientAge) && (
              <Grid item xs={12}>
                <ChildDetails
                  admit={admit}
                  setAdmit={setAdmit}
                  handleChangeInput={handleChangeInput}
                  isChild={isChild}
                  checkDisabled={checkDisabled}
                  disabled={disabled}
                  patientIsSelected={patientIsSelected}
                  isAddOnlyPatient={isAddOnlyPatient}
                  mergeToNewCreatedPatient={mergeToNewCreatedPatient}
                />
              </Grid>
            )}

            {config?.admitPatient.adultAccordion &&
              admit.dob &&
              !isChild(patientAge) && (
                <Grid item xs={12} sx={{ m: 2 }}>
                  <AdultAccordion
                    admit={admit}
                    setAdmit={setAdmit}
                    handleChangeInput={handleChangeInput}
                    isChild={isChild}
                    checkDisabled={checkDisabled}
                  />
                </Grid>
              )}

            {isRemoveDialogOpen && (
              <RemoveNextOfKinEntryModal
                admit={admit}
                onRemoveNok={() => {
                  handleRemoveNok(removedIndex);
                  closeRemoveDialog();
                  setRemovedIndex(null);
                }}
                removedIndex={removedIndex}
                closeDialog={closeRemoveDialog}
                isDialogOpen={isRemoveDialogOpen}
                onSetRemovedIndex={setRemovedIndex}
              />
            )}
          </Grid>
        </AgyleAccordion>
      </Grid>
    </Grid>
  );
};

export default DemographicDetails;
